import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { NotebookPen, Building2, Users, Upload, Trash2, Crown, FileText, Sparkles, Search, FolderLock } from "lucide-react";
import TeamResourcesCard from "@/components/TeamResourcesCard";

export default function MentorTeamNotes() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gaps, setGaps] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/mentor/my-teams").then(({ data }) => setTeams(data || [])).catch(() => setTeams([])),
      api.get("/mentor/resource-gaps").then(({ data }) => setGaps(data?.team_ids || [])).catch(() => setGaps([])),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-[#525252]" data-testid="mentor-team-notes-loading">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="mentor-team-notes-page">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><NotebookPen size={12} /> Mentor · shared notes</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2 text-[#0F1F2E]">
          Your teams&apos; notes
        </h1>
        <p className="text-[#525252] mt-2 max-w-2xl text-sm leading-relaxed">
          Every team you&apos;re assigned to, with their shared notes and files.
          Add reflections after a call, drop in a transcript, or upload something
          you promised the team you&apos;d share.
        </p>
      </div>

      {teams.length === 0 ? (
        <div className="border-2 border-dashed border-[#f4b847] bg-[#FEF9E7] p-8" data-testid="mentor-team-notes-empty">
          <div className="flex items-center gap-3">
            <Sparkles size={22} className="text-[#7A5B17]" />
            <div>
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#7A5B17]">Not assigned yet</div>
              <h2 className="font-display text-2xl font-bold text-[#7A5B17] mt-1">No teams assigned to you yet.</h2>
              <p className="text-sm text-[#7A5B17] mt-2 max-w-xl">
                You&apos;ll appear here the moment your admin allocates you to a team.
                Team assignments are usually revealed at the end of Session 2.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {gaps.length > 0 && (
            <div className="border-2 border-[#e93e36] bg-[#FDEDED] p-4 flex items-center gap-3" data-testid="mentor-resource-gap-alert">
              <span className="min-w-[28px] h-7 px-2 flex items-center justify-center bg-[#e93e36] text-white text-sm font-bold" data-testid="mentor-resource-gap-count">{gaps.length}</span>
              <div className="text-sm text-[#7A1F1F]">
                <strong>{gaps.length === 1 ? "1 team has" : `${gaps.length} teams have`} no sponsor resources yet.</strong>{" "}
                Drop the sponsor brief, data or decks into the Team Resources box below so students can get started.
              </div>
            </div>
          )}
          {teams.map((t) => (
            <div key={t.id} className="space-y-4">
              <TeamNotesBlock team={t} currentUserId={user?.id} missingResources={gaps.includes(t.id)} />
              <TeamResourcesCard teamId={t.id} mentorAssigned />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamNotesBlock({ team, currentUserId, missingResources }) {
  const confirm = useConfirm();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [fileTitle, setFileTitle] = useState("");
  const [file, setFile] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/teams/${team.id}/notes`);
      setNotes(data || []);
    } catch { setNotes([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [team.id]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post(`/teams/${team.id}/notes`, { title: title.trim(), content: content.trim() || null });
      toast.success("Note added");
      setTitle(""); setContent("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note");
    } finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", fileTitle.trim() || file.name);
      await api.post(`/teams/${team.id}/notes/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      setFile(null); setFileTitle("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload file");
    } finally { setBusy(false); }
  };

  const del = async (n) => {
    if (!(await confirm({ title: `Delete "${n.title}"?`, destructive: true }))) return;
    try {
      await api.delete(`/teams/${team.id}/notes/${n.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? notes.filter((n) =>
        (n.title || "").toLowerCase().includes(q) ||
        (n.content || "").toLowerCase().includes(q) ||
        (n.author_name || "").toLowerCase().includes(q) ||
        (n.file_name || "").toLowerCase().includes(q))
    : notes;

  return (
    <div className="border border-[#0A0A0A] bg-white p-6 space-y-5" data-testid={`mentor-team-block-${team.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap pb-4 border-b border-[#E5E7EB]">
        <div className="min-w-0">
          <div className="label-eyebrow flex items-center gap-2 flex-wrap"><Users size={12} /> Team {team.letter || ""}
            {missingResources && (
              <span className="inline-flex items-center gap-1 bg-[#e93e36] text-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider normal-case" data-testid={`mentor-team-no-resources-${team.id}`}>
                <FolderLock size={9} /> 0 sponsor resources
              </span>
            )}
          </div>
          <div className="font-display text-2xl font-black tracking-tighter mt-1 text-[#0F1F2E]" data-testid={`mentor-team-name-${team.id}`}>
            {team.name}
          </div>
          {team.business_name && (
            <div className="flex items-center gap-1.5 text-sm text-[#525252] mt-1">
              <Building2 size={12} /> {team.business_name}
            </div>
          )}
          {team.challenge_statement && (
            <p className="text-xs text-[#525252] mt-2 max-w-2xl whitespace-pre-wrap leading-relaxed">
              {team.challenge_statement}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#525252]">Members</div>
          <div className="font-display text-2xl font-black text-[#0F1F2E]">{(team.members || []).length}</div>
        </div>
      </div>

      {/* Members */}
      {(team.members || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {team.members.map((m) => (
            <div key={m.id} className="inline-flex items-center gap-1.5 border border-[#E5E7EB] bg-[#FAFAFA] px-2.5 py-1 text-xs" data-testid={`mentor-team-member-${m.id}`}>
              {m.is_team_leader && <Crown size={10} className="text-[#f4b847]" />}
              <span className="font-medium text-[#0F1F2E]">{m.name || m.email}</span>
            </div>
          ))}
        </div>
      )}

      {/* Forms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <form onSubmit={addNote} className="border border-[#E5E7EB] p-4 space-y-3" data-testid={`mentor-note-form-${team.id}`}>
          <div className="label-eyebrow">Add a note</div>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Note title (e.g. 'Kick-off call notes')" className="rounded-none h-11"
            data-testid={`mentor-note-title-${team.id}`}/>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="Key decisions, actions, next steps…"
            className="rounded-none min-h-[100px]"
            data-testid={`mentor-note-content-${team.id}`}/>
          <Button type="submit" disabled={busy} className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]"
            data-testid={`mentor-note-add-${team.id}`}>
            {busy ? "Saving…" : "Add note"}
          </Button>
        </form>

        <form onSubmit={uploadFile} className="border border-[#E5E7EB] p-4 space-y-3" data-testid={`mentor-upload-form-${team.id}`}>
          <div className="label-eyebrow">Upload a file</div>
          <Input value={fileTitle} onChange={(e) => setFileTitle(e.target.value)}
            placeholder="File title (optional)" className="rounded-none h-11"
            data-testid={`mentor-upload-title-${team.id}`}/>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm border border-[#E5E7EB] p-2"
            data-testid={`mentor-upload-file-${team.id}`}/>
          <p className="text-[11px] text-[#525252]">Up to 20 MB. Recordings, transcripts, decks.</p>
          <Button type="submit" disabled={busy || !file} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]"
            data-testid={`mentor-upload-btn-${team.id}`}>
            <Upload size={14} className="mr-1.5" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </form>
      </div>

      {/* Notes list */}
      <div>
        {loading ? (
          <div className="text-sm text-[#525252]">Loading notes…</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-[#525252] italic">No notes yet for this team. Add the first meeting summary above.</div>
        ) : (
          <>
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search notes, files or author…"
                className="rounded-none h-10 pl-9"
                data-testid={`mentor-notes-search-${team.id}`}/>
            </div>
            {filtered.length === 0 ? (
              <div className="text-sm text-[#525252] italic">No notes match &quot;{query}&quot;.</div>
            ) : (
              <ul className="divide-y divide-[#E5E7EB]" data-testid={`mentor-notes-list-${team.id}`}>
                {filtered.map((n) => (
                  <li key={n.id} className="py-3 flex items-start justify-between gap-3" data-testid={`mentor-note-${n.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider bg-[#0F1F2E] text-white px-1.5 py-0.5 font-bold">
                          {n.kind === "file" ? "File" : "Note"}
                        </span>
                        <div className="font-medium text-[#0F1F2E]">{n.title}</div>
                      </div>
                      {n.content && <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap mt-2 leading-relaxed">{n.content}</p>}
                      {n.file_url && (
                        <a href={n.file_url} target="_blank" rel="noreferrer"
                           className="text-sm text-[#2596be] hover:underline mt-2 inline-flex items-center gap-1"
                           data-testid={`mentor-note-file-${n.id}`}>
                          <FileText size={12} /> {n.file_name || "Download"} ({Math.round((n.file_size || 0) / 1024)} KB)
                        </a>
                      )}
                      <div className="text-[11px] uppercase tracking-wider text-[#525252] mt-2">
                        {n.author_name} · {n.author_role} · {new Date(n.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    {n.author_id === currentUserId && (
                      <button onClick={() => del(n)} className="text-[#A23232] hover:text-[#7A1F1F]"
                              data-testid={`mentor-note-delete-${n.id}`} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
